import { CartItems } from '@/scripts/cart/cart-items'
import { CartDrawer, syncCartLifestyle } from '@/scripts/cart/cart-drawer'
import { ATTRIBUTES, qsOptional, SELECTORS } from '@/scripts/core/global'
import { getSectionHTMLForResource } from '@/scripts/core/cart-functions'
import { enqueueCartOperation, cancelPendingDebounces } from '@/scripts/cart/cart-queue'

// Module-level singleton: only one rebuy:cart.change listener on document,
// delegates to the current CartDrawerItems instance. Prevents duplicate
// listeners when outerHTML replacement destroys and recreates the element.
let currentDrawerItems: CartDrawerItems | null = null
let rebuyListenerRegistered = false

function registerRebuyListener() {
	if (rebuyListenerRegistered) return
	rebuyListenerRegistered = true
	document.addEventListener('rebuy:cart.change', () => {
		currentDrawerItems?.onRebuyCartChange()
	})
}

export class CartDrawerItems extends CartItems {
	static override htmlSelector = 'cart-drawer-items'
	static override selectors = {
		...CartItems.selectors,
		element: 'cart-drawer-items',
		lineItemStatus: '[data-uc-cart-drawer-status]',
		errors: '[data-uc-cart-drawer-errors]',
		liveRegionText: '[data-uc-cart-drawer-live-region-text]',
		main: '[data-uc-cart-drawer-main]',
		// the following selectors are partial - they will be concatenated with the line ID
		line: '#CartDrawer-Item', // ex: `${this.instanceSelectors.line}-${line}`
		lineQuantity: '#CartDrawer-Quantity', // ex: `${this.instanceSelectors.lineQuantity}-${line}`
		lineError: '#CartDrawer-LineItemError', // ex: `${this.instanceSelectors.lineError}-${line}`
	}

	private lastKnownItemCount: number | null = null

	constructor() {
		super()
	}
	override setInstanceSelectors() {
		this.instanceSelectors = CartDrawerItems.selectors
	}
	override getSectionsToRender() {
		return [
			{
				id: 'CartDrawerMain',
				section: 'cart-drawer',
				selector: '[data-uc-cart-drawer-main]',
			},
			{
				id: 'CartDrawerItemCount',
				section: 'cart-drawer',
				selector: '[data-item-count]',
			},
			{
				id: 'CartDrawerTotals',
				section: 'cart-drawer',
				selector: '[data-uc-cart-drawer-totals]',
			},
			{
				id: 'CartIconBubble',
				section: 'cart-icon-bubble',
				selector: SELECTORS.cartLink,
			},
		]
	}

	protected override isCartEmpty(parsedState: { item_count: number; sections?: Record<string, string> }): boolean {
		const sectionHtml = parsedState.sections?.['cart-drawer']
		if (sectionHtml) {
			const doc = new DOMParser().parseFromString(sectionHtml, 'text/html')
			return doc.querySelector('cart-drawer-items[data-uc-cart-empty]') !== null
		}
		return super.isCartEmpty(parsedState)
	}

	override connectedCallback() {
		super.connectedCallback()
		currentDrawerItems = this
		registerRebuyListener()
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		if (currentDrawerItems === this) {
			currentDrawerItems = null
		}
	}

	onRebuyCartChange() {
		// Rebuy fires this event after it modifies the cart (e.g. removing
		// sibling bundle items). If the item count changed since the last
		// known state, refresh the cart drawer contents in place.
		// Cancel any pending debounced changes — Rebuy's reload will fetch
		// fresh server state, making optimistic updates stale.
		cancelPendingDebounces()

		if (this.lastKnownItemCount === null) return

		void enqueueCartOperation(async () => {
			const res = await fetch('/cart.js')
			const cart = await res.json()

			if (cart.item_count !== this.lastKnownItemCount) {
				this.lastKnownItemCount = cart.item_count
				await this.reloadDrawerInPlace()
			}
		})
	}

	private async reloadDrawerInPlace() {
		const html = await getSectionHTMLForResource(window.location.href, 'cart-drawer')
		if (!html) return

		const parser = new DOMParser()
		const doc = parser.parseFromString(html, 'text/html')

		// Determine emptiness from server-rendered HTML (excludes free samples)
		const isEmpty = doc.querySelector('cart-drawer-items[data-uc-cart-empty]') !== null

		// Replace only the targeted areas (items, count, totals) so that
		// cart-samples and the checkout button are not destroyed/re-created.
		const targetSelectors = ['[data-uc-cart-drawer-main]', '[data-item-count]', '[data-uc-cart-drawer-totals]', 'shipping-bar-wrapper']
		for (const selector of targetSelectors) {
			const fresh = qsOptional(selector, doc)
			const current = qsOptional(selector)
			if (fresh && current) {
				current.innerHTML = fresh.innerHTML
			}
		}

		// Update the cart icon bubble
		const freshBubble = qsOptional(SELECTORS.cartLink, doc)
		const currentBubble = qsOptional(SELECTORS.cartLink)
		if (freshBubble && currentBubble) {
			currentBubble.innerHTML = freshBubble.innerHTML
		}

		// Update empty state and re-attach listeners on the fresh DOM
		const cartDrawer = qsOptional<CartDrawer>('cart-drawer')
		if (cartDrawer) {
			if (isEmpty) {
				cartDrawer.setAttribute(ATTRIBUTES.cartEmpty, '')
			} else {
				cartDrawer.removeAttribute(ATTRIBUTES.cartEmpty)
			}
			// Sync cart-samples presence (e.g. first add to empty cart)
			const freshSamples = doc.querySelector('cart-samples')
			const currentSamples = cartDrawer.querySelector('cart-samples')
			if (freshSamples && !currentSamples) {
				const ctas = cartDrawer.querySelector('.cart-drawer__ctas')
				if (ctas) {
					ctas.parentElement!.insertBefore(freshSamples, ctas)
				}
			} else if (!freshSamples && currentSamples) {
				currentSamples.remove()
			}
			syncCartLifestyle(cartDrawer, doc)
			cartDrawer.setCloseButtonListener()
			void window.Ucoast.mediaManager.loadAllInContainer(cartDrawer)
		}
	}

	override updateQuantity(line: string, quantity: string, name?: string, lineItemKey?: string) {
		// Track item count before the update so we can detect server-side changes
		this.lastKnownItemCount = this.querySelectorAll(this.instanceSelectors.item).length
		if (quantity === '0') {
			// Account for the item we're removing
			this.lastKnownItemCount -= 1
		}
		super.updateQuantity(line, quantity, name, lineItemKey)
	}
}
